import React, { useState, useEffect, useCallback, useMemo } from "react";
import {
  Box,
  Typography,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  FormControlLabel,
  Checkbox,
  Alert,
  Divider,
  CircularProgress,
  useTheme,
} from "@mui/material";
import { Clear as ClearIcon, Add as AddIcon } from "@mui/icons-material";
import { useQuery, useMutation } from "@apollo/client";
import {
  GET_RISK_LIBRARY,
  ADD_RISKS_FROM_LIBRARY,
} from "../../../../../../apis/organization-apis/risk-apis/riskApis";
import { GET_MASTER_LOOKUP_BY_TYPE } from "../../../../../../apis/common-apis/commonApis";
import { GET_STAFF_DETAILS } from "../../../../../../apis/organization-apis/people-apis/peopleApis";
import FilterBar from "../../../../../../components/FilterNSearchBar/FilterNSearchBar";
import { LevelSelectForm } from "../../../../../../components/LevelSelectForm/LevelSelectForm";
import LibraryRiskItems from "./LibraryRiskItems";
import DialogSkeleton from "../../../../../../components/Skeletons/DialogSkeleton";
import { MasterLookupItemsType } from "../../../../../../utils/enums";
import Role from "../../../../../../utils/roles";

export default function AddRiskFromLibraryDialog({
  open,
  onClose,
  onAddRisks,
  existingRisks = [],
  setSnackbarInfo,
}) {
  const theme = useTheme();
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedCategoryId, setSelectedCategoryId] = useState("All");
  const [selectedRisks, setSelectedRisks] = useState([]);
  const [riskOwnerEmail, setRiskOwnerEmail] = useState("");
  const [ownerError, setOwnerError] = useState("");
  const [categories, setCategories] = useState([{ id: "All", title: "All" }]);

  const { loading, error, data } = useQuery(GET_RISK_LIBRARY, {
    variables: { filter: {}, page: 1, limit: 1000 },
    fetchPolicy: "network-only",
  });
  const { data: riskCategoriesData } = useQuery(GET_MASTER_LOOKUP_BY_TYPE, {
    variables: { type: MasterLookupItemsType.RISK_CATEGORY },
  });
  const { data: staffData, loading: staffLoading } = useQuery(GET_STAFF_DETAILS);
  const [addRisksFromLibrary, { loading: addLoading }] = useMutation(
    ADD_RISKS_FROM_LIBRARY
  );

  const adminEmails = useMemo(
    () =>
      staffData?.getEmployees?.data
        ?.filter((employee) =>
          employee?.roles?.some(
            (role) => role?.role_description === Role.ADMIN
          )
        )
        .map((employee) => employee?.emails?.[0]?.email)
        .filter(Boolean) || [],
    [staffData]
  );

  const allRisks = (data?.getRiskLibrary?.data || [])?.map((risk) => ({
    id: risk?.id,
    title: risk?.title,
    category_title: risk?.risk_category_title,
    description: risk?.description,
    key: risk?.key,
  }));

  const isRiskExisting = useCallback(
    (masterRisk) =>
      existingRisks?.some(
        (existingRisk) => existingRisk?.key === masterRisk?.key
      ),
    [existingRisks]
  );

  const nonExistingSelectedCount = selectedRisks?.filter((riskKey) => {
    const risk = allRisks?.find((r) => r?.key === riskKey);
    return risk && !isRiskExisting(risk);
  })?.length;

  useEffect(() => {
    if (open && data) {
      const existingRiskKeys = (allRisks ?? [])
        ?.filter((risk) => isRiskExisting(risk))
        ?.map((risk) => risk?.key);
      setSelectedRisks(existingRiskKeys);
    }
  }, [open, existingRisks, isRiskExisting]);

  useEffect(() => {
    const riskCategories = riskCategoriesData?.getMasterLookupByType?.data?.map(
      (cat) => ({ id: cat?.id, title: cat?.title })
    );
    setCategories([{ id: "All", title: "All" }, ...(riskCategories || [])]);
  }, [riskCategoriesData]);

  const filteredRisks = allRisks?.filter((risk) => {
    const categoryMatch =
      selectedCategoryId === "All" ||
      categories?.find((cat) => cat?.id === selectedCategoryId)?.title ===
        risk?.category_title;
    const searchMatch =
      !searchQuery ||
      (risk?.title ?? "").toLowerCase().includes(searchQuery.toLowerCase()) ||
      (risk?.description ?? "")
        .toLowerCase()
        .includes(searchQuery.toLowerCase()) ||
      (risk?.category_title ?? "")
        .toLowerCase()
        .includes(searchQuery.toLowerCase());
    return categoryMatch && searchMatch;
  });

  const handleRiskToggle = (riskKey) => {
    setSelectedRisks((prev) =>
      prev?.includes(riskKey)
        ? prev?.filter((key) => key !== riskKey)
        : [...(prev || []), riskKey]
    );
  };

  const handleSelectAll = () => {
    const risks = filteredRisks;
    const nonExistingRisks = risks?.filter((risk) => !isRiskExisting(risk));
    const selectedNonExistingRisks = selectedRisks?.filter((riskKey) => {
      const risk = risks?.find((r) => r?.key === riskKey);
      return risk && !isRiskExisting(risk);
    });
    if (selectedNonExistingRisks?.length === nonExistingRisks?.length) {
      const existingRiskKeys = allRisks
        ?.filter((risk) => isRiskExisting(risk))
        ?.map((risk) => risk?.key);
      setSelectedRisks(existingRiskKeys);
    } else {
      const allRiskKeys = allRisks?.map((risk) => risk?.key);
      setSelectedRisks(allRiskKeys);
    }
  };

  const handleAddRisks = async () => {
    const selectedMasterRisks = allRisks?.filter(
      (risk) => selectedRisks?.includes(risk?.key) && !isRiskExisting(risk)
    );
    const riskKeys = selectedMasterRisks?.map((risk) => risk?.key);
    if (riskKeys?.length === 0) return;

    if (!riskOwnerEmail?.trim()) {
      setOwnerError("Risk owner email is required");
      return;
    }
    setOwnerError("");

    try {
      const { data: mutationData } = await addRisksFromLibrary({
        variables: {
          risk_keys: riskKeys,
          risk_owner_email: riskOwnerEmail.trim(),
        },
      });
      setSnackbarInfo({
        message:
          mutationData?.addRisksFromLibrary?.message ||
          "Risks added successfully.",
        vertical: "top",
        horizontal: "right",
      });
      await onAddRisks(selectedMasterRisks);
      handleClose();
    } catch (err) {
      setSnackbarInfo({
        message: err.message || "Failed to add risks. Please try again.",
        vertical: "top",
        horizontal: "right",
      });
    }
  };

  const clearFilters = () => {
    setSearchQuery("");
    setSelectedCategoryId("All");
  };

  const handleFilterChange = (filter) => {
    const category = categories?.find((cat) => cat?.title === filter);
    setSelectedCategoryId(category ? category?.id : "All");
  };

  const handleClose = () => {
    setSelectedRisks([]);
    setRiskOwnerEmail("");
    setOwnerError("");
    setSearchQuery("");
    setSelectedCategoryId("All");
    onClose();
  };

  const filterOptions = categories?.map((cat) => cat?.title);

  return (
    <>
      <Dialog open={open} onClose={handleClose} maxWidth="lg" fullWidth>
        <DialogTitle
          sx={{
            fontWeight: "bold",
            fontSize: "16px",
            color: theme.customColors.text.strong,
            background: theme.customColors.background.cardHeader,
            borderBottom: `1px solid ${theme.customColors.border.main}`,
          }}
        >
          Add Risks from Library
          <IconButton
            aria-label="close"
            onClick={handleClose}
            sx={{
              position: "absolute",
              right: 10,
              top: 10,
              color: theme.palette.grey[500],
            }}
          >
            <ClearIcon sx={{ fontSize: 16 }} />
          </IconButton>
        </DialogTitle>
        <DialogContent
          dividers
          sx={{ background: theme.customColors.background.main }}
        >
          {loading ? (
            <DialogSkeleton open={open} onClose={handleClose} />
          ) : error ? (
            <Alert
              severity="error"
              sx={{
                mb: 3,
                backgroundColor: theme.customColors.status.error.bg,
                border: `1px solid ${theme.customColors.border.error}`,
              }}
            >
              Failed to load risk library. Please try again later.
            </Alert>
          ) : (
            <>
              <Alert
                severity="info"
                sx={{
                  mb: 3,
                  backgroundColor: theme.customColors.background.info,
                  border: `1px solid ${theme.customColors.border.accent}`,
                }}
              >
                <Typography
                  variant="body2"
                  fontWeight={600}
                  sx={{
                    fontSize: "14px",
                    color: theme.customColors.text.strong,
                  }}
                >
                  Select risks from our master library to add to your risk
                  register. Risks that are already in your register are shown as
                  checked and disabled.
                </Typography>
              </Alert>
              <FilterBar
                searchQuery={searchQuery}
                onSearchChange={(e) => setSearchQuery(e.target.value)}
                filterLabel="Categories:"
                filters={filterOptions}
                selectedFilter={
                  selectedCategoryId === "All"
                    ? null
                    : categories.find((cat) => cat.id === selectedCategoryId)
                        ?.title
                }
                onFilterChange={handleFilterChange}
                onClearFilters={clearFilters}
                countLabel={`${filteredRisks.length} risks found`}
              />
              {filteredRisks.length > 0 && (
                <Box sx={{ mb: 2 }}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={(() => {
                          const nonExistingRisks = filteredRisks.filter(
                            (risk) => !isRiskExisting(risk)
                          );
                          const selectedNonExistingRisks = selectedRisks.filter(
                            (riskKey) => {
                              const risk = filteredRisks.find(
                                (r) => r.key === riskKey
                              );
                              return risk && !isRiskExisting(risk);
                            }
                          );
                          return (
                            selectedNonExistingRisks.length ===
                              nonExistingRisks.length &&
                            nonExistingRisks.length > 0
                          );
                        })()}
                        indeterminate={(() => {
                          const nonExistingRisks = filteredRisks.filter(
                            (risk) => !isRiskExisting(risk)
                          );
                          const selectedNonExistingRisks = selectedRisks.filter(
                            (riskKey) => {
                              const risk = filteredRisks.find(
                                (r) => r.key === riskKey
                              );
                              return risk && !isRiskExisting(risk);
                            }
                          );
                          return (
                            selectedNonExistingRisks.length > 0 &&
                            selectedNonExistingRisks.length <
                              nonExistingRisks.length
                          );
                        })()}
                        onChange={handleSelectAll}
                      />
                    }
                    label={`Select All (${(() => {
                      const nonExistingRisks = filteredRisks.filter(
                        (risk) => !isRiskExisting(risk)
                      );
                      const selectedNonExistingRisks = selectedRisks.filter(
                        (riskKey) => {
                          const risk = filteredRisks.find(
                            (r) => r.key === riskKey
                          );
                          return risk && !isRiskExisting(risk);
                        }
                      );
                      return `${selectedNonExistingRisks.length}/${nonExistingRisks.length} available`;
                    })()})`}
                    sx={{
                      "& .MuiFormControlLabel-label": { fontSize: "14px" },
                    }}
                  />
                </Box>
              )}
              <Divider sx={{ mb: 2 }} />
              <LibraryRiskItems
                risks={filteredRisks}
                selectedRisks={selectedRisks}
                isRiskExisting={isRiskExisting}
                onToggle={handleRiskToggle}
              />
            </>
          )}
        </DialogContent>
        <DialogActions
          sx={{
            px: 3,
            py: 2,
            display: "block",
            background: theme.customColors.background.cardHeader,
            borderTop: `1px solid ${theme.customColors.border.main}`,
          }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              gap: 2,
              width: "100%",
              flexWrap: "wrap",
            }}
          >
            {nonExistingSelectedCount > 0 && !loading && !error ? (
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 2,
                  flex: 1,
                  minWidth: 280,
                }}
              >
                <Typography
                  variant="body2"
                  sx={{
                    fontSize: "13px",
                    fontWeight: 600,
                    color: theme.customColors.text.secondary,
                    whiteSpace: "nowrap",
                    flexShrink: 0,
                  }}
                >
                  Assign owner ({nonExistingSelectedCount})
                </Typography>
                <Box sx={{ width: 280, flexShrink: 0 }}>
                  <LevelSelectForm
                    label="Risk Owner Email"
                    options={adminEmails.map((email) => ({
                      value: email,
                      label: email,
                    }))}
                    value={riskOwnerEmail}
                    onChange={(value) => {
                      setRiskOwnerEmail(value);
                      if (ownerError) setOwnerError("");
                    }}
                    disabled={staffLoading || addLoading}
                  />
                </Box>
              </Box>
            ) : (
              <Box sx={{ flex: 1 }} />
            )}
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1.5,
                flexShrink: 0,
              }}
            >
              <Button
                onClick={handleClose}
                variant="outlined"
                sx={{ fontSize: "14px" }}
                disabled={addLoading}
              >
                Cancel
              </Button>
              <Button
                onClick={handleAddRisks}
                variant="contained"
                disabled={
                  addLoading ||
                  nonExistingSelectedCount === 0 ||
                  loading ||
                  error ||
                  staffLoading ||
                  !riskOwnerEmail?.trim()
                }
                startIcon={
                  addLoading ? (
                    <CircularProgress size={16} />
                  ) : (
                    <AddIcon sx={{ fontSize: 16 }} />
                  )
                }
                sx={{ fontSize: "14px", fontWeight: 600 }}
              >
                {addLoading
                  ? "Adding..."
                  : `Add Selected (${nonExistingSelectedCount})`}
              </Button>
            </Box>
          </Box>
          {ownerError && (
            <Typography
              variant="caption"
              color="error"
              sx={{ mt: 1, display: "block", fontSize: "12px" }}
            >
              {ownerError}
            </Typography>
          )}
        </DialogActions>
      </Dialog>
    </>
  );
}
